import type { ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { TopStatusBar } from '../../components/domain/TopStatusBar';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { useAccounts } from '../accounts/queries';
import { useMe } from '../auth/queries';
import { useLatestSnapshot } from '../snapshots/queries';

import { ReviewDetail } from './ReviewDetail';
import { ReviewsList } from './ReviewsList';
import { usePendingReviews, useReviewsList } from './queries';

type ReviewTab = 'pending' | 'submitted' | 'all';

export function ReviewsPage() {
  const params = useParams();
  const navigate = useNavigate();
  const me = useMe();
  const accounts = useAccounts();
  const account = accounts.data?.[0] ?? null;
  const snapshot = useLatestSnapshot(account?.id ?? null);
  const pendingSummary = usePendingReviews();
  const [tab, setTab] = useState<ReviewTab>('pending');
  const [symbol, setSymbol] = useState('');
  const reviews = useReviewsList(tab, tab === 'all' && symbol.trim() ? symbol.trim() : undefined);
  const selectedTradeId = params.tradeId ?? reviews.data?.[0]?.tradeId ?? null;
  const selectedReview = useMemo(
    () => reviews.data?.find((review) => review.tradeId === selectedTradeId) ?? null,
    [reviews.data, selectedTradeId],
  );

  useEffect(() => {
    if (!params.tradeId && reviews.data?.[0]) {
      navigate(`/reviews/${reviews.data[0].tradeId}`, { replace: true });
    }
  }, [navigate, params.tradeId, reviews.data]);

  if (me.isLoading || accounts.isLoading) {
    return <main className="p-6 text-text-muted">Loading reviews...</main>;
  }

  if (me.isError || accounts.isError || !me.data) {
    return <main className="p-6 text-danger">Could not load reviews.</main>;
  }

  return (
    <main className="min-h-screen bg-bg text-text">
      <TopStatusBar currency={account?.currency ?? me.data.baseCurrency} snapshot={snapshot.data} />
      <div className="mx-auto max-w-7xl space-y-5 px-4 py-6">
        <header>
          <h1 className="text-2xl font-semibold">Trade Reviews</h1>
          <p className="mt-1 text-sm text-text-muted">
            Backfill pending closes and edit prior reflections.
          </p>
        </header>

        <div className="grid gap-5 lg:grid-cols-[24rem_minmax(0,1fr)]">
          <Card className="p-0">
            <div className="border-b border-border p-3">
              <div className="grid grid-cols-3 gap-1 rounded border border-border bg-bg-muted p-1">
                <TabButton active={tab === 'pending'} onClick={() => setTab('pending')}>
                  Pending ({pendingSummary.data?.count ?? 0})
                </TabButton>
                <TabButton active={tab === 'submitted'} onClick={() => setTab('submitted')}>
                  Recent
                </TabButton>
                <TabButton active={tab === 'all'} onClick={() => setTab('all')}>
                  All
                </TabButton>
              </div>
              {tab === 'all' ? (
                <input
                  className="mt-3 h-10 w-full rounded border border-border bg-bg px-3 text-sm"
                  placeholder="Search symbol"
                  value={symbol}
                  onChange={(event) => setSymbol(event.target.value.toUpperCase())}
                />
              ) : null}
            </div>
            {reviews.isLoading ? (
              <p className="p-4 text-sm text-text-muted">Loading review list...</p>
            ) : (
              <ReviewsList
                reviews={reviews.data ?? []}
                selectedTradeId={selectedTradeId}
                onSelect={(tradeId) => navigate(`/reviews/${tradeId}`)}
              />
            )}
          </Card>
          <ReviewDetail
            tradeId={selectedTradeId}
            submitted={Boolean(selectedReview?.submittedAt) || tab !== 'pending'}
          />
        </div>
      </div>
    </main>
  );
}

function TabButton({
  active,
  children,
  onClick,
}: {
  active: boolean;
  children: ReactNode;
  onClick: () => void;
}) {
  return (
    <Button type="button" size="sm" variant={active ? 'primary' : 'ghost'} onClick={onClick}>
      {children}
    </Button>
  );
}
